import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { StatusGood } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { AppButton, SimpleModal } from '@Components/Control';

function AdminDemoCreateModal({
  small, onClose, onSubmit, submitting = false, generatedUrl = null,
}) {
  const { theme: { page, button } } = useAppTheme();
  const [campaignName, setCampaignName] = React.useState('');
  const [days, setDays] = React.useState('30');
  const [copied, setCopied] = React.useState(false);

  const isValid = campaignName.trim().length > 0 && parseInt(days, 10) >= 1;

  const handleSubmit = React.useCallback(() => {
    if (!isValid) return;
    onSubmit(campaignName.trim(), parseInt(days, 10));
  }, [campaignName, days, isValid, onSubmit]);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard?.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedUrl]);

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.6rem',
    background: page.inputBg,
    color: page.inputText,
    border: `1px solid ${page.inputBorder}`,
    borderRadius: 'var(--app-radius)',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <SimpleModal
      small={small}
      toggleModal={(open) => { if (!open) onClose(); }}
      modalTitle={generatedUrl ? 'Campaign Created' : 'New Demo Campaign'}
      textColor={page.primaryText}
      highlightColor={page.highlight}
      modalBg={page.modalBg}
      hideLowerSection
      desktopMinWidth="32rem"
    >
      {generatedUrl ? (
        <Box direction="column" gap="1rem" pad={{ vertical: '1rem' }}>
          <Box direction="row" gap="0.5rem" align="center">
            <StatusGood color="status-ok" size="medium" />
            <Text size="0.875rem" color={page.primaryText} weight={500}>
              Your campaign link is ready — copy it and share with your prospects.
            </Text>
          </Box>

          <Box
            background={page.inputBg}
            border={{ color: page.inputBorder, size: '1px', style: 'solid' }}
            round="xsmall"
            pad={{ vertical: '0.6rem', horizontal: '0.75rem' }}
          >
            <Text
              size="0.82rem"
              color={page.primaryText}
              style={{ fontFamily: 'monospace', wordBreak: 'break-all', userSelect: 'all' }}
            >
              {generatedUrl}
            </Text>
          </Box>

          <Box
            direction="row"
            justify="end"
            align="center"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'top',
            }}
            pad={{ top: '1rem' }}
            gap="0.5rem"
          >
            <AppButton
              overrideHover
              level="tableControls"
              label="Close"
              color={button.primaryText}
              bgColor={button.primaryBg}
              onClick={onClose}
            />
            <AppButton
              overrideHover
              level="tableControls"
              label={copied ? 'Copied!' : 'Copy Link'}
              color={button.primaryText}
              bgColor={copied ? '#16a34a' : page.highlight}
              onClick={handleCopy}
            />
          </Box>
        </Box>
      ) : (
        <Box direction="column" gap="1rem" pad={{ vertical: '1rem' }}>
          <Text size="0.70rem" color={page.secondaryText}>
            Generate a shareable demo link for an outreach campaign.
            Everyone who visits the link will fill in their name, email, and YouTube channel.
          </Text>

          <Box gap="xsmall">
            <Text size="0.8rem" color={page.primaryText} weight={500}>Campaign name</Text>
            <input
              type="text"
              placeholder="e.g. Q3 Email Outreach"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              style={inputStyle}
            />
          </Box>

          <Box gap="xsmall">
            <Text size="0.8rem" color={page.primaryText} weight={500}>Link expiry (days)</Text>
            <input
              type="number"
              min="1"
              max="365"
              placeholder="30"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              style={{ ...inputStyle, width: '120px' }}
            />
          </Box>

          <Box
            direction="row"
            justify="end"
            align="center"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'top',
            }}
            pad={{ top: '1rem' }}
            gap="0.5rem"
          >
            <AppButton
              overrideHover
              level="tableControls"
              label="Cancel"
              color={button.primaryText}
              bgColor={button.primaryBg}
              onClick={onClose}
            />
            <AppButton
              overrideHover
              level="tableControls"
              label={submitting ? 'Generating…' : 'Generate Link'}
              disabled={!isValid || submitting}
              color={button.primaryText}
              bgColor={page.highlight}
              onClick={handleSubmit}
            />
          </Box>
        </Box>
      )}
    </SimpleModal>
  );
}

AdminDemoCreateModal.propTypes = {
  small: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  generatedUrl: PropTypes.string,
};

export default AdminDemoCreateModal;
